/**
 * Token Detail types for the token detail page API.
 *
 * Story 4.1: Token Detail API — Hybrid Data Assembly
 *
 * The token detail endpoint assembles data from 3 sources:
 * 1. D1 (TokenRepository) — off-chain metadata
 * 2. IIndexerService — on-chain stats from V4 subgraph
 * 3. IHolderIndexerService — holder data from ERC20 Transfer indexer
 *
 * Each section carries its own dataSource metadata.
 */

import { z } from "zod";
import { DataSourceMetaSchema, PriceIntervalSchema } from "./indexer";

// ─── Token Detail Response ──────────────────────────────────

export const TokenDetailMetadataSchema = z.object({
	id: z.string(),
	contractAddress: z.string(),
	poolAddress: z.string(),
	creatorWallet: z.string(),
	chainId: z.number(),
	name: z.string(),
	symbol: z.string(),
	description: z.string().nullable(),
	tagline: z.string().nullable(),
	category: z.string().nullable(),
	logoUrl: z.string().nullable(),
	hookType: z.string(),
	mevModule: z.string().nullable(),
	feeBpsCreator: z.number(),
	feeBpsTreasury: z.number(),
	feeBpsProtocol: z.number(),
	deployTxHash: z.string(),
	status: z.string(),
	verifiedAt: z.string().nullable(),
	verifiedBy: z.string().nullable(),
	socialLinks: z.unknown().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	dataSource: z.literal("database"),
});
export type TokenDetailMetadata = z.infer<typeof TokenDetailMetadataSchema>;

export const TokenDetailStatsSchema = z.object({
	price: z.number(),
	priceChange24h: z.number(),
	volume24h: z.number(),
	volume7d: z.number(),
	marketCap: z.number(),
	holderCount: z.number(),
	liquidity: z.number(),
	totalSupply: z.string(),
	feesUsd24h: z.number(),
	txCount24h: z.number(),
	dataSource: z.enum(["indexer", "cache", "seeded"]),
	dataAge: z.number(),
	stale: z.boolean(),
});
export type TokenDetailStats = z.infer<typeof TokenDetailStatsSchema>;

export const TokenDetailResponseSchema = z.object({
	metadata: TokenDetailMetadataSchema,
	stats: TokenDetailStatsSchema.nullable(),
});
export type TokenDetailResponse = z.infer<typeof TokenDetailResponseSchema>;

// ─── Price History Params ───────────────────────────────────

export const PriceHistoryParamsSchema = z.object({
	interval: PriceIntervalSchema,
	limit: z.coerce.number().min(1).max(500).default(48),
});
export type PriceHistoryParams = z.infer<typeof PriceHistoryParamsSchema>;

// ─── Trade History Params ───────────────────────────────────

export const TradeHistoryParamsSchema = z.object({
	limit: z.coerce.number().min(1).max(100).default(50),
});
export type TradeHistoryParams = z.infer<typeof TradeHistoryParamsSchema>;

// ─── Holder List Params ─────────────────────────────────────

export const HolderListParamsSchema = z.object({
	limit: z.coerce.number().min(1).max(100).default(50),
});
export type HolderListParams = z.infer<typeof HolderListParamsSchema>;

// ─── Indexed Response with DataSource ───────────────────────

export const IndexedDataResponseSchema = z.object({
	meta: DataSourceMetaSchema,
});
